import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, User, Mail, Phone, MapPin, Clock, Send, MessageSquare, Briefcase, ChevronRight, Users, CheckCircle } from 'lucide-react';
import { formatPhoneDisplay } from '@/utils/phoneUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700' },
  assigned: { label: 'Assigned', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'In Progress', color: 'bg-yellow-100 text-yellow-700' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' }
};

const AssignedLeadDashboard = () => {
  const navigate = useNavigate();
  const { providerId, assignmentId } = useParams();
  const { token } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAssignments();
  }, []);

  useEffect(() => {
    if (providerId && assignments.length > 0) {
      const provider = getProviders().find(p => p.id === providerId);
      setSelectedProvider(provider);
    }
  }, [providerId, assignments]);

  useEffect(() => {
    if (assignmentId && assignments.length > 0) {
      const assignment = assignments.find(a => a.id === assignmentId);
      if (assignment) {
        setSelectedAssignment(assignment);
        fetchComments(assignmentId);
      }
    }
  }, [assignmentId, assignments]);

  const fetchAssignments = async () => {
    try {
      const response = await axios.get(`${API}/leads/assignments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Sort assignments by created_at (newest first)
      const sortedAssignments = response.data.sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA; // Descending order (newest first)
      });
      
      setAssignments(sortedAssignments);
    } catch (error) {
      console.error('Failed to fetch assignments:', error);
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (assignmentId) => {
    try {
      const response = await axios.get(`${API}/assignments/${assignmentId}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      toast.error('Failed to load comments');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/assignments/${selectedAssignment.id}/comments`,
        { comment: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComments([...comments, response.data]);
      setNewComment('');
      toast.success('Comment added successfully');
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const getProviders = () => {
    const providerMap = new Map();
    assignments.forEach(assignment => {
      if (!providerMap.has(assignment.provider_id)) {
        providerMap.set(assignment.provider_id, {
          id: assignment.provider_id,
          name: assignment.provider_name,
          service_type: assignment.service_type,
          leads: []
        });
      }
      providerMap.get(assignment.provider_id).leads.push(assignment);
    });
    return Array.from(providerMap.values());
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-stone-500">Loading...</p>
        </div>
      </Layout>
    );
  }

  // View 3: Individual Lead Detail with Comments
  if (assignmentId && selectedAssignment) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto p-6">
          <Button
            onClick={() => navigate(`/assigned-leads/${providerId}`)}
            variant="ghost"
            className="mb-4"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to Provider Leads
          </Button>

          <div className="mb-6">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-stone-900 mb-2">Lead Details</h1>
            <p className="text-stone-600">Assigned to {selectedAssignment.provider_name}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Lead Information */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="p-6">
                <h2 className="text-xl font-bold text-stone-900 mb-4 flex items-center gap-2">
                  <User size={24} className="text-[#1A4D2E]" />
                  Visitor Information
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="text-stone-600 text-sm">Full Name</label>
                    <p className="text-lg font-semibold text-stone-900">{selectedAssignment.visitor_name}</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-stone-600 text-sm flex items-center gap-2">
                        <Mail size={14} />
                        Email
                      </label>
                      <p className="text-stone-900">{selectedAssignment.visitor_email}</p>
                    </div>
                    <div>
                      <label className="text-stone-600 text-sm flex items-center gap-2">
                        <Phone size={14} />
                        Phone
                      </label>
                      <p className="text-stone-900">{formatPhoneDisplay(selectedAssignment.visitor_phone)}</p>
                    </div>
                  </div>

                  <div>
                    <label className="text-stone-600 text-sm flex items-center gap-2">
                      <MapPin size={14} />
                      Property Address
                    </label>
                    <p className="text-stone-900">{selectedAssignment.property_address || 'Not provided'}</p>
                  </div>

                  {selectedAssignment.description && (
                    <div>
                      <label className="text-stone-600 text-sm">Description</label>
                      <p className="text-stone-900">{selectedAssignment.description}</p>
                    </div>
                  )}

                  <div>
                    <label className="text-stone-600 text-sm flex items-center gap-2">
                      <Clock size={14} />
                      Assigned On
                    </label>
                    <p className="text-stone-900">{formatDate(selectedAssignment.created_at)}</p>
                  </div>
                </div>
              </Card>

              {/* Activity History */}
              <Card className="p-6">
                <h2 className="text-xl font-bold text-stone-900 mb-4 flex items-center gap-2">
                  <MessageSquare size={24} className="text-[#1A4D2E]" />
                  Activity History
                </h2>

                {/* Add Comment Form */}
                <form onSubmit={handleAddComment} className="mb-6">
                  <div className="flex gap-2">
                    <Input
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment or update..."
                      className="flex-1"
                      disabled={submitting}
                    />
                    <Button
                      type="submit"
                      disabled={submitting || !newComment.trim()}
                      className="bg-[#1A4D2E] hover:bg-[#143D24] text-white"
                    >
                      <Send size={18} className="mr-2" />
                      {submitting ? 'Posting...' : 'Post'}
                    </Button>
                  </div>
                </form>

                {/* Comments List */}
                <div className="space-y-4">
                  {comments.length === 0 ? (
                    <div className="text-center py-8 text-stone-500">
                      <MessageSquare size={48} className="mx-auto mb-2 text-stone-300" />
                      <p>No activity yet. Be the first to add an update!</p>
                    </div>
                  ) : (
                    comments.map((comment) => (
                      <div key={comment.id} className="border-l-4 border-[#1A4D2E] pl-4 py-2 bg-stone-50 rounded-r">
                        <div className="flex items-start justify-between mb-1">
                          <span className="font-semibold text-stone-900">{comment.author_name}</span>
                          <span className="text-xs text-stone-500">{formatDate(comment.created_at)}</span>
                        </div>
                        <p className="text-stone-700">{comment.comment}</p>
                        <span className="text-xs text-stone-500 capitalize">{comment.author_type}</span>
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </div>

            {/* Sidebar - Status */}
            <div className="space-y-6">
              <Card className="p-6">
                <h3 className="text-lg font-bold text-stone-900 mb-4">Lead Status</h3>
                <div className={`px-4 py-3 rounded-lg text-center font-semibold ${statusConfig[selectedAssignment.status]?.color}`}>
                  {statusConfig[selectedAssignment.status]?.label}
                </div>
              </Card>

              <Card className="p-6 bg-stone-50">
                <h3 className="text-lg font-bold text-stone-900 mb-3">Assignment Details</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-stone-600">Service Type:</span>
                    <p className="font-semibold text-stone-900">{selectedAssignment.service_type}</p>
                  </div>
                  <div>
                    <span className="text-stone-600">Provider:</span>
                    <p className="font-semibold text-stone-900">{selectedAssignment.provider_name}</p>
                  </div>
                  <div>
                    <span className="text-stone-600">Last Updated:</span>
                    <p className="font-semibold text-stone-900">{formatDate(selectedAssignment.updated_at)}</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // View 2: Provider's Leads List
  if (providerId && selectedProvider) {
    const providerLeads = assignments.filter(a => a.provider_id === providerId);

    return (
      <Layout>
        <div className="max-w-7xl mx-auto p-6">
          <Button
            onClick={() => navigate('/assigned-leads')}
            variant="ghost"
            className="mb-4"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to All Providers
          </Button>

          <div className="mb-6">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-stone-900 mb-2">{selectedProvider.name}</h1>
            <p className="text-stone-600">{selectedProvider.service_type} • {providerLeads.length} assigned lead(s)</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {Object.entries(statusConfig).map(([status, config]) => {
              const count = providerLeads.filter(a => a.status === status).length;
              return (
                <Card key={status} className="p-4">
                  <div className="text-sm text-stone-600 mb-1">{config.label}</div>
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold text-stone-900">{count}</div>
                </Card>
              );
            })}
          </div>

          {/* Leads List */}
          <div className="space-y-4">
            {providerLeads.length === 0 ? (
              <Card className="p-12 text-center">
                <Users size={64} className="mx-auto text-stone-300 mb-4" />
                <h3 className="text-xl font-semibold text-stone-900 mb-2">No Leads</h3>
                <p className="text-stone-600">No leads assigned to this provider yet.</p>
              </Card>
            ) : (
              providerLeads.map((assignment) => (
                <Card
                  key={assignment.id}
                  className="p-6 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/assigned-leads/${providerId}/${assignment.id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-xl font-bold text-stone-900">{assignment.visitor_name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusConfig[assignment.status]?.color}`}>
                          {statusConfig[assignment.status]?.label}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-stone-600 mb-3">
                        <div>
                          <p className="font-medium text-stone-700">Contact:</p>
                          <p>{assignment.visitor_email}</p>
                          <p>{formatPhoneDisplay(assignment.visitor_phone)}</p>
                        </div>
                        <div>
                          <p className="font-medium text-stone-700">Property:</p>
                          <p>{assignment.property_address || 'N/A'}</p>
                        </div>
                      </div>

                      <div className="text-xs text-stone-500">
                        Assigned {formatDate(assignment.created_at)}
                      </div>
                    </div>

                    <ChevronRight size={24} className="text-stone-400" />
                  </div>
                </Card>
              ))
            )}
          </div>
        </div>
      </Layout>
    );
  }

  // View 1: All Providers List
  const providers = getProviders();

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-stone-900 mb-2">Assigned Lead Dashboard</h1>
          <p className="text-stone-600">View and manage all leads assigned to service providers</p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Total Providers</p>
                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-700">{providers.length}</p>
              </div>
              <Briefcase size={32} className="text-blue-400" />
            </div>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Total Leads</p>
                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-green-700">{assignments.length}</p>
              </div>
              <Users size={32} className="text-green-400" />
            </div>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600 font-medium">In Progress</p>
                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-yellow-700">
                  {assignments.filter(a => a.status === 'in_progress').length}
                </p>
              </div>
              <Clock size={32} className="text-yellow-400" />
            </div>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">Completed</p>
                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-purple-700">
                  {assignments.filter(a => a.status === 'completed').length}
                </p>
              </div>
              <CheckCircle size={32} className="text-purple-400" />
            </div>
          </Card>
        </div>

        {/* Providers List */}
        {providers.length === 0 ? (
          <Card className="p-12 text-center">
            <Briefcase size={64} className="mx-auto text-stone-300 mb-4" />
            <h3 className="text-xl font-semibold text-stone-900 mb-2">No Assigned Leads Yet</h3>
            <p className="text-stone-600">Leads you assign to service providers will appear here.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {providers.map((provider) => (
              <Card
                key={provider.id}
                className="p-6 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/assigned-leads/${provider.id}`)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-stone-900 mb-1">{provider.name}</h3>
                    <p className="text-sm text-stone-600">{provider.service_type}</p>
                  </div>
                  <ChevronRight size={24} className="text-stone-400" />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-stone-600">Total Leads:</span>
                    <span className="font-semibold text-stone-900">{provider.leads.length}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-stone-600">In Progress:</span>
                    <span className="font-semibold text-yellow-700">
                      {provider.leads.filter(l => l.status === 'in_progress').length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-stone-600">Completed:</span>
                    <span className="font-semibold text-green-700">
                      {provider.leads.filter(l => l.status === 'completed').length}
                    </span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AssignedLeadDashboard;
